import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import _ from "lodash";
import { getFooter } from "../../../utils/helperUtils";
import {
    formatCurrencyUSD,
    formatNumberPdf,
    formatPercentage,
} from "../../../utils/formattingUtils";

const styles = StyleSheet.create({
    page: {
        padding: "30px 40px",
        backgroundColor: "#ffffff",
        position: "relative",
    },
    headerSection: {
        marginBottom: 10,
        textAlign: "center",
        paddingBottom: 5,
    },
    mainHeading: {
        fontSize: 15,
        fontWeight: "bold",
        color: "#2c3e50",
        marginBottom: 4,
        textAlign: "center",
    },
    subHeading: {
        fontSize: 12,
        color: "#34495e",
        marginBottom: 4,
    },
    tableSection: {
        marginBottom: 15,
        borderRadius: 4,
        overflow: "hidden",
        minHeight: "auto",
    },
    mainContainer: {
        flex: 1,
        paddingBottom: 40,
        display: "flex",
        flexDirection: "column",
    },
    tableHeader: {
        backgroundColor: "#f7f6f8",
        fontWeight: "bold",
        fontSize: 13,
    },
    tableCell: {
        padding: "3px 6px",
        fontSize: 10,
        borderBottom: "1px solid #e0e0e0",
        textAlign: "right",
    },
    labelCell: {
        textAlign: "left",
        fontWeight: "bold",
    },
    totalRow: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
    },
});

interface SpaceData {
    enable?: boolean;
    spaceType: string;
    constructionBuildingClass: string;
    constructionBuildingQuality: string;
    noOfStories: number;
    baseSqFtCost: number;
    heightAndSizeRefinement: {
        clearHeightPerStoryMultiplier: number;
        floorAreaPerimeterMultiplier: number;
        combinedHeightAndSizeMultiplier: number;
        adjustedRate: number;
        currentCostMultiplier: number;
        localMultiplier: number;
        combinedMultiplier: number;
        finalSqFtCost: number;
        area: number;
        replacementCost: number;
    };
}

interface DepreciationData {
    effectivePhysicalDepreciation: number;
    effectivePhysicalDepreciationAmount: number;
    functionalDepreciationAmount: number;
    economicObsolescenceAmount: number;
}

interface Feature {
    featureType: string;
    number?: number;
    areaSqFt?: number;
    baseRate: number;
    adjustedRate?: number;
    cost: number;
}

interface FeaturesData {
    features: Record<string, Feature>;
    summary: {
        physicalDepreciation: number;
        functionalDepreciation: number;
        economicObsolescence: number;
        other: number;
        miscellaneous: number;
    };
}

interface SiteImprovement {
    improvementType: string;
    areaSqFt: number;
    perUnit: number;
    perUnitAdjusted: number;
    total: number;
}

interface SiteImprovementData {
    siteImprovements: Record<string, SiteImprovement>;
    summary: {
        physicalDepreciation: number;
        functionalDepreciation: number;
        economicObsolescence: number;
        otherLumpSumForSiteImprovements: number;
        miscellaneous: number;
    };
}

interface SummaryData {
    totalReplacementCost: number;
    adjustedDepreciatedCost: number;
    additionalFeaturesSiteImprovements: number;
    landValue: number;
    improvementValue: number;
    finalValue: number;
}

interface CostApproachPageProps {
    data: any;
    confidentialInfo: string;
}

const TableHeaderRow: React.FC<{ labels: string[] }> = ({ labels }) => (
    <TR>
        {labels.map((label, index) => (
            <TD key={index} style={styles.tableCell}>
                {label}
            </TD>
        ))}
    </TR>
);

const PropertyHeader: React.FC<{ data: any }> = ({ data }) => {
    const coverPage = _.get(
        data,
        "appealApproachCalculationData.exportAppealsPackage.coverPage",
        {}
    );
    const address = coverPage.subjectPropertyAddress || {};

    return (
        <View style={styles.headerSection}>
            <Text style={styles.mainHeading}>{coverPage.subjectPropertyNumber}</Text>
            <Text style={styles.subHeading}>Marshall and Swift Cost Estimation</Text>
            <Text style={styles.subHeading}>Replacement Building Cost Analysis</Text>
            <Text style={{ ...styles.subHeading, fontSize: 8 }}>
                {`${address.streetaddress}, ${address.city}, ${address.state} ${address.zipCode}`}
            </Text>
        </View>
    );
};

const IndustrialBuildUpSection: React.FC<{ space: SpaceData }> = ({ space }) => {
    const rows = [
        ["Construction/Building Class", space.constructionBuildingClass],
        ["Construction/Building Quality", space.constructionBuildingQuality],
        ["No. of Stories", formatNumberPdf(space.noOfStories)],
        ["Base Square Foot Cost", formatCurrencyUSD(space.baseSqFtCost)],
        [
            "Height Adjustments",
            formatNumberPdf(space.heightAndSizeRefinement.clearHeightPerStoryMultiplier),
        ],
        [
            "Area/Perimeter Adjustments",
            formatNumberPdf(space.heightAndSizeRefinement.floorAreaPerimeterMultiplier),
        ],
        [
            "Combined Height And Size Adjustments",
            formatNumberPdf(space.heightAndSizeRefinement.combinedHeightAndSizeMultiplier),
        ],
        ["Adjusted Base Rate", formatCurrencyUSD(space.heightAndSizeRefinement.adjustedRate)],
        [
            "M & S Current Cost Multiplier",
            formatNumberPdf(space.heightAndSizeRefinement.currentCostMultiplier),
        ],
        ["M & S Local Multiplier", formatNumberPdf(space.heightAndSizeRefinement.localMultiplier)],
        [
            "M & S Current & Local Multiplier",
            formatNumberPdf(space.heightAndSizeRefinement.combinedMultiplier),
        ],
        ["Final Base Rate", formatCurrencyUSD(space.heightAndSizeRefinement.finalSqFtCost)],
        ["Building Area", formatNumberPdf(space.heightAndSizeRefinement.area)],
        ["Base Building Cost", formatCurrencyUSD(space.heightAndSizeRefinement.replacementCost)],
    ];

    return (
        <View style={styles.tableSection}>
            <Table style={{ border: "none" }}>
                <TH>
                    <TD
                        style={{
                            ...styles.tableCell,
                            ...styles.tableHeader,
                            width: "100%",
                            textAlign: "left",
                            borderRight: "none", // Remove right border
                        }}
                    >
                        {space.spaceType}
                    </TD>
                </TH>
                <TableHeaderRow labels={["Description", "Value"]} />
                {rows.map((row, index) => (
                    <TR key={index}>
                        <TD style={{ ...styles.tableCell, ...styles.labelCell }}>{row[0]}</TD>
                        <TD style={styles.tableCell}>{row[1]}</TD>
                    </TR>
                ))}
            </Table>
        </View>
    );
};

const DepreciationSection: React.FC<{
    spaceComputationData: Record<string, DepreciationData>;
    propertyDetails: { yearBuilt: string; effectiveYear: string };
}> = ({ spaceComputationData, propertyDetails }) => {
    const currentYear = new Date().getFullYear();
    const actualAge = currentYear - parseInt(propertyDetails.yearBuilt);
    const effectiveAge = currentYear - parseInt(propertyDetails.effectiveYear);

    let totalPhysical = 0;
    let totalFunctional = 0;
    let totalEconomic = 0;

    Object.values(spaceComputationData).forEach(space => {
        totalPhysical += space.effectivePhysicalDepreciationAmount;
        totalFunctional += space.functionalDepreciationAmount;
        totalEconomic += space.economicObsolescenceAmount;
    });

    const rows = [
        {
            type: "Effective Physical Depreciation",
            actualAge,
            effectiveAge,
            percent: formatPercentage(
                spaceComputationData[Object.keys(spaceComputationData)[0]]
                    ?.effectivePhysicalDepreciation || 0
            ),
            amount: formatCurrencyUSD(totalPhysical),
        },
        {
            type: "Additional Functional Depreciation",
            actualAge,
            effectiveAge,
            percent: "",
            amount: formatCurrencyUSD(totalFunctional),
        },
        {
            type: "Additional Economic Depreciation",
            actualAge,
            effectiveAge,
            percent: "",
            amount: formatCurrencyUSD(totalEconomic),
        },
        {
            type: "Total Depreciation",
            actualAge: "",
            effectiveAge: "",
            percent: "",
            amount: formatCurrencyUSD(totalPhysical + totalFunctional + totalEconomic),
        },
    ];

    return (
        <View style={styles.tableSection}>
            <Table style={{ border: "none" }}>
                <TH>
                    <TD
                        style={{
                            ...styles.tableCell,
                            ...styles.tableHeader,
                            width: "100%",
                            textAlign: "left",
                            borderRight: "none", // Remove right border
                        }}
                    >
                        Depreciation and Adjustments
                    </TD>
                </TH>
                <TableHeaderRow
                    labels={["Type", "Actual Age", "Effective Age", "Percent", "Amount"]}
                />
                {rows.map((row, index) => (
                    <TR key={index} style={index === rows.length - 1 ? styles.totalRow : undefined}>
                        <TD style={{ ...styles.tableCell, ...styles.labelCell }}>{row.type}</TD>
                        <TD style={styles.tableCell}>{row.actualAge}</TD>
                        <TD style={styles.tableCell}>{row.effectiveAge}</TD>
                        <TD style={styles.tableCell}>{row.percent}</TD>
                        <TD style={styles.tableCell}>{row.amount}</TD>
                    </TR>
                ))}
            </Table>
        </View>
    );
};

const FeaturesSection: React.FC<{ featuresData: FeaturesData }> = ({ featuresData }) => {
    const { features, summary } = featuresData;
    const totalDepreciation =
        summary.physicalDepreciation +
        summary.functionalDepreciation +
        summary.economicObsolescence;

    const calculateDepreciatedCost = (cost: number) =>
        Math.round(((100 - totalDepreciation) * cost) / 100);

    const rows = [
        ...Object.values(features).map(feature => ({
            type: feature.featureType,
            quantity: feature.number || feature.areaSqFt || "",
            baseRate: formatCurrencyUSD(feature.baseRate),
            adjustedRate: feature.adjustedRate ? formatCurrencyUSD(feature.adjustedRate) : "",
            replacementCost: formatCurrencyUSD(feature.cost),
            depreciation: `${totalDepreciation}%`,
            cost: formatCurrencyUSD(calculateDepreciatedCost(feature.cost)),
        })),
        {
            type: "Extra Features",
            quantity: "-",
            baseRate: "-",
            adjustedRate: "-",
            replacementCost: formatCurrencyUSD(summary.other + summary.miscellaneous),
            depreciation: `${totalDepreciation}%`,
            cost: formatCurrencyUSD(
                calculateDepreciatedCost(summary.other + summary.miscellaneous)
            ),
        },
    ];

    return (
        <View style={styles.tableSection}>
            <Table style={{ border: "none" }}>
                <TH>
                    <TD
                        style={{
                            ...styles.tableCell,
                            ...styles.tableHeader,
                            width: "100%",
                            textAlign: "left",
                            borderRight: "none", // Remove right border
                        }}
                    >
                        Features
                    </TD>
                </TH>
                <TableHeaderRow
                    labels={[
                        "Type",
                        "Area(SQFT) / Quantity",
                        "Base Rate",
                        "Adjusted Rate",
                        "Replacement Cost",
                        "Depreciation",
                        "Cost",
                    ]}
                />
                {rows.map((row, index) => (
                    <TR key={index}>
                        <TD style={{ ...styles.tableCell, ...styles.labelCell }}>{row.type}</TD>
                        <TD style={styles.tableCell}>{row.quantity}</TD>
                        <TD style={styles.tableCell}>{row.baseRate}</TD>
                        <TD style={styles.tableCell}>{row.adjustedRate}</TD>
                        <TD style={styles.tableCell}>{row.replacementCost}</TD>
                        <TD style={styles.tableCell}>{row.depreciation}</TD>
                        <TD style={styles.tableCell}>{row.cost}</TD>
                    </TR>
                ))}
            </Table>
        </View>
    );
};

const SiteImprovementsSection: React.FC<{ siteImprovementData: SiteImprovementData }> = ({
    siteImprovementData,
}) => {
    const { siteImprovements, summary } = siteImprovementData;
    const totalDepreciation =
        summary.physicalDepreciation +
        summary.functionalDepreciation +
        summary.economicObsolescence;

    const calculateDepreciatedCost = (cost: number) =>
        Math.round(((100 - totalDepreciation) * cost) / 100);

    const rows = [
        ...Object.values(siteImprovements).map(improvement => ({
            type: improvement.improvementType,
            area: formatNumberPdf(improvement.areaSqFt),
            baseRate: formatCurrencyUSD(improvement.perUnit),
            adjustedRate: formatCurrencyUSD(improvement.perUnitAdjusted),
            replacementCost: formatCurrencyUSD(improvement.total),
            depreciation: `${totalDepreciation}%`,
            cost: formatCurrencyUSD(calculateDepreciatedCost(improvement.total)),
        })),
        {
            type: "Extra Site Improvements",
            area: "-",
            baseRate: "-",
            adjustedRate: "-",
            replacementCost: formatCurrencyUSD(
                summary.otherLumpSumForSiteImprovements + summary.miscellaneous
            ),
            depreciation: `${totalDepreciation}%`,
            cost: formatCurrencyUSD(
                calculateDepreciatedCost(
                    summary.otherLumpSumForSiteImprovements + summary.miscellaneous
                )
            ),
        },
    ];

    return (
        <View style={styles.tableSection}>
            <Table style={{ border: "none" }}>
                <TH>
                    <TD
                        style={{
                            ...styles.tableCell,
                            ...styles.tableHeader,
                            width: "100%",
                            textAlign: "left",
                            borderRight: "none", // Remove right border
                        }}
                    >
                        Site Improvements
                    </TD>
                </TH>
                <TableHeaderRow
                    labels={[
                        "Type",
                        "Area(SQFT)",
                        "Base Rate",
                        "Adjusted Rate",
                        "Replacement Cost",
                        "Depreciation",
                        "Cost",
                    ]}
                />
                {rows.map((row, index) => (
                    <TR key={index}>
                        <TD style={{ ...styles.tableCell, ...styles.labelCell }}>{row.type}</TD>
                        <TD style={styles.tableCell}>{row.area}</TD>
                        <TD style={styles.tableCell}>{row.baseRate}</TD>
                        <TD style={styles.tableCell}>{row.adjustedRate}</TD>
                        <TD style={styles.tableCell}>{row.replacementCost}</TD>
                        <TD style={styles.tableCell}>{row.depreciation}</TD>
                        <TD style={styles.tableCell}>{row.cost}</TD>
                    </TR>
                ))}
            </Table>
        </View>
    );
};

const SummarySection: React.FC<{ summary: SummaryData }> = ({ summary }) => {
    const rows = [
        {
            description: "Total Replacement Cost",
            value: formatCurrencyUSD(summary.totalReplacementCost),
        },
        {
            description: "Adjusted Depreciated Cost",
            value: formatCurrencyUSD(summary.adjustedDepreciatedCost),
        },
        {
            description: "Features & Site Improvements Cost",
            value: formatCurrencyUSD(summary.additionalFeaturesSiteImprovements),
        },
        {
            description: "Total Land Value",
            value: formatCurrencyUSD(summary.landValue),
        },
        {
            description: "Total Improvement Value",
            value: formatCurrencyUSD(summary.improvementValue),
        },
        {
            description: "Total Property Value",
            value: formatCurrencyUSD(summary.finalValue),
        },
    ];

    return (
        <View style={styles.tableSection}>
            <Table style={{ border: "none" }}>
                <TH>
                    <TD
                        style={{
                            ...styles.tableCell,
                            ...styles.tableHeader,
                            width: "100%",
                            textAlign: "left",
                            borderRight: "none", // Remove right border
                        }}
                    >
                        Summary
                    </TD>
                </TH>
                {rows.map((row, index) => (
                    <TR key={index} style={index === rows.length - 1 ? styles.totalRow : undefined}>
                        <TD style={{ ...styles.tableCell, ...styles.labelCell }}>
                            {row.description}
                        </TD>
                        <TD style={styles.tableCell}>{row.value}</TD>
                    </TR>
                ))}
            </Table>
        </View>
    );
};

export const CostApproachPage: React.FC<CostApproachPageProps> = ({ data, confidentialInfo }) => {
    const costApproachData = _.get(data, "appealApproachCalculationData.cost.costApproachData", {});
    const propertyDetails = _.get(data, "appealApproachCalculationData.cost.propertyDetails", {});

    const {
        spaceComputationData,
        siteImprovementComputationData,
        featuresComputationData,
        summary,
    } = costApproachData;

    const enabledSpaces = Object.keys(spaceComputationData)
        .map(key => spaceComputationData[key])
        .filter(space => space.enable === true);

    const chunkedSpaces = _.chunk(enabledSpaces, 2);

    return (
        <>
            {/* Pages for spaces (2 per page) */}
            {chunkedSpaces.map((spaceChunk, pageIndex) => (
                <Page key={pageIndex} size="A4" style={styles.page}>
                    <View style={styles.mainContainer} wrap={false}>
                        <PropertyHeader data={data} />
                        <Text style={styles.mainHeading}>INDUSTRIAL BUILD UP COSTS</Text>

                        {spaceChunk.map(space => (
                            <IndustrialBuildUpSection space={space} />
                        ))}

                        {/* Add depreciation section if it's the last page and has less than 2 spaces */}
                        {pageIndex === chunkedSpaces.length - 1 && spaceChunk.length < 2 && (
                            <DepreciationSection
                                spaceComputationData={spaceComputationData}
                                propertyDetails={propertyDetails}
                            />
                        )}
                    </View>
                    {getFooter(confidentialInfo)}
                </Page>
            ))}

            {/* Separate depreciation page if last space page had 2 spaces */}
            {chunkedSpaces[chunkedSpaces.length - 1]?.length === 2 && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.mainContainer} wrap={false}>
                        <PropertyHeader data={data} />
                        <Text style={styles.mainHeading}>INDUSTRIAL BUILD UP COSTS</Text>

                        <DepreciationSection
                            spaceComputationData={spaceComputationData}
                            propertyDetails={propertyDetails}
                        />
                    </View>
                    {getFooter(confidentialInfo)}
                </Page>
            )}

            {/* Features and Site Improvements page */}
            <Page size="A4" style={styles.page}>
                <View style={styles.mainContainer} wrap={false}>
                    <PropertyHeader data={data} />
                    <Text style={styles.mainHeading}>FEATURES AND SITE IMPROVEMENTS</Text>

                    <FeaturesSection featuresData={featuresComputationData} />

                    <SiteImprovementsSection siteImprovementData={siteImprovementComputationData} />
                </View>
                {getFooter(confidentialInfo)}
            </Page>

            {/* Summary page */}
            <Page size="A4" style={styles.page}>
                <View style={styles.mainContainer} wrap={false}>
                    <PropertyHeader data={data} />
                    <Text style={styles.mainHeading}>COST APPROACH SUMMARY</Text>

                    <SummarySection summary={summary} />
                </View>
                {getFooter(confidentialInfo)}
            </Page>
        </>
    );
};
